import { Link } from 'react-router-dom'
import { useFormSchema } from '@/features/admin/forms/hooks'
import { DualListPicker } from '@/components/common/DualListPicker'
import { useSetProductSections } from '@/features/product/hooks'
import type { FormSection } from '@/features/contract/types'

function is404(err: unknown): boolean {
  return (err as { response?: { status?: number } } | null)?.response?.status === 404
}

export function ProductSectionsPanel({ productCode }: { productCode: string }) {
  // All product (object_type='product') sections; assigned = this product's sections.
  // A brand-new object type / unassigned product returns 404 -> treat as empty.
  const allQ = useFormSchema('product')
  const assignedQ = useFormSchema(`product/${productCode}`)
  const setSections = useSetProductSections(productCode)

  if (allQ.isLoading) return <div>Ładowanie sekcji...</div>
  if (allQ.error && !is404(allQ.error)) return <div className="text-red-600">Błąd ładowania sekcji.</div>

  const all: FormSection[] = allQ.data?.sections ?? []
  const selected: FormSection[] = assignedQ.data?.sections ?? []
  const selectedIds = new Set(selected.map(s => s.id))
  const available = all.filter(s => !selectedIds.has(s.id))

  return (
    <div className="flex flex-col gap-3 text-sm">
      <p className="text-xs text-slate-500">
        Przeciągnij sekcje, które ma zawierać formularz tego produktu (elementu). Kolejność na prawym panelu = kolejność wyświetlania.
        Sekcje produktu definiujesz w{' '}
        <Link to="/settings/forms/product" className="text-sky-700 underline">Formularze → Produkt</Link>.
      </p>

      <DualListPicker
        available={available}
        selected={selected}
        getId={s => s.id}
        renderItem={s => (
          <div className="flex items-baseline gap-2">
            <span>{s.labelPl}</span>
            <span className="font-mono text-[10px] text-slate-400">{s.code}</span>
          </div>
        )}
        onChange={ids => setSections.mutate(ids.map(Number))}
        availableLabel="Dostępne sekcje"
        selectedLabel="Aktywne w tym produkcie"
        emptyAvailable={
          all.length === 0 ? (
            <span>
              Brak sekcji produktu - dodaj w{' '}
              <Link to="/settings/forms/product" className="text-sky-700 underline">Formularze → Produkt</Link>.
            </span>
          ) : (
            <span>Wszystkie sekcje są podpięte</span>
          )
        }
        emptySelected={<span>Przeciągnij sekcje tutaj</span>}
      />
    </div>
  )
}
